import React, { useState } from "react";
import { IconButton, Stack, TooltipHost, Icon } from "@fluentui/react";
import IMessage from "../../../models/IMessage";
import { ReactionType } from "../../../enums/ReactionType";
import { Rating } from "../../../enums/Rating";
import IBotService from "../../../interfaces/IBotService";
import { BotServiceFactory } from "../../../services/BotServiceFactory";
import { FeedbackRequest } from "../../../models/FeedbackRequest";
import IFeedbackResponse from "../../../models/IFeedbackResponse";
import Constants from "../../../constants/Constansts";
import LikeFeedback from "./LikeFeedback";
import DislikeFeedback from "./DislikeFeedback";
import PDFGeneratorService from "../../../services/PDFGeneratorService";
import FeedbackOptionConstants from "../../../constants/FeedbackOptionConstants";
import { ChatMode } from "../../../enums/ChatMode";

interface IResponseActionProps {
    MessageProps: IMessage;
    messageRef: React.RefObject<HTMLDivElement>;

}

const ResponseAction: React.FC<IResponseActionProps> = ({ messageRef, MessageProps }) => {
    const [isLikeDialogVisible, setIsLikeDialogVisible] = useState(false);
    const [isDislikeDialogVisible, setIsDislikeDialogVisible] = useState(false);
    const [reaction, setReaction] = useState<ReactionType>();
    const [isLiked, setIsLiked] = useState(false);
    const [isDisliked, setIsDisliked] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [selectedLikeOptions, setSelectedLikeOptions] = useState<string[]>([]);
    const [selectedDislikeOptions, setSelectedDislikeOptions] = useState<string[]>([]);
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (messageRef.current) {
            try {
                await navigator.clipboard.writeText(messageRef.current.innerText);
                setCopied(true);
                setTimeout(() => setCopied(false), 10000);
            } catch (err) {
                console.error('Failed to copy: ', err);
            }
        }
    };

    const handleLike = () => {
        setReaction(ReactionType.Like);
        setIsLikeDialogVisible(true);
    };

    const handleDislike = () => {
        setReaction(ReactionType.Dislike);
        setIsDislikeDialogVisible(true);
    };

    const handleDialogDismiss = () => {
        setIsLikeDialogVisible(false);
        setIsDislikeDialogVisible(false);
        setFeedback('');
        setSelectedLikeOptions([]);
        setSelectedDislikeOptions([]);
    };

    const handleReaction = (reaction: ReactionType | undefined) => {
        if (reaction === ReactionType.Like) {
            setIsLiked(true);
            setIsDisliked(false);
        } else if (reaction === ReactionType.Dislike) {
            setIsDisliked(true);
            setIsLiked(false);
        }
    };

    const submitFeedback = async (rating: Rating, options: string[], feedback: string): Promise<IFeedbackResponse> => {
        try {
            const pdfService = new PDFGeneratorService();
            const botService: IBotService = BotServiceFactory.getBotService(Constants.USE_MOCK_SERVICE);
            const { chat_mode, message_id, conversation_id } = MessageProps;

            // Map the options to their corresponding text values
            const mappedOptions = options.map(optionKey => {
                const option = [...FeedbackOptionConstants.likeOptions, ...FeedbackOptionConstants.dislikeOptions].find(opt => opt.key === optionKey);
                return option ? option.text : optionKey;
            });
      
            let base64Pdf: string | undefined = undefined;
            if (shouldIncludePdf(mappedOptions, chat_mode , Constants.FEEDBACK_PDF_CONDITIONS)) {
                base64Pdf = await pdfService.exportHtmlToBase64('content');
            }
            const feedbackRequest: FeedbackRequest = {
                chat_mode,
                message_id,
                conversation_id,
                rating,
                comment: feedback,
                tags: mappedOptions,
                ...(base64Pdf && { pdf_file: base64Pdf })
            };

            const responseMessage: IFeedbackResponse = await botService.postFeedback(feedbackRequest);
            handleReaction(reaction);
            handleDialogDismiss();
            return responseMessage;
        } catch (error: any) {
            console.error('Error submitting feedback:', error);
            return { conversation_id: "", message_id: "" };
        }
    };

    function shouldIncludePdf(tags: string[], chatMode: ChatMode, pdfConditions: Record<ChatMode, string[]>): boolean {

        const conditions = pdfConditions[chatMode] || [];
        return tags.some(tag => conditions.includes(tag));
    }

    const handleLikeFeedbackSubmit = (options: string[], feedback: string) => {
        if (options.length === 0 || (options.includes('other') && !feedback)) {
            alert('Please complete the feedback.');
            return;
        }
        submitFeedback(Rating.Like, options, feedback);
    };

    const handleDislikeFeedbackSubmit = (options: string[], feedback: string) => {
        if (options.length === 0 || (options.includes('other') && !feedback)) {
            alert('Please complete the feedback.');
            return;
        }

        submitFeedback(Rating.Dislike, options, feedback);
        console.log("options", options);

    };

    return (
        <Stack>
            <Stack horizontal>
                <TooltipHost content="Copy">
                    <IconButton iconProps={{ iconName: 'Copy' }} onClick={handleCopy} />
                    {copied && <span style={{ color: 'green' }}><Icon iconName="SkypeCircleCheck" /> Copied successfully!</span>}
                </TooltipHost>
                <TooltipHost content="Like">
                    <IconButton iconProps={{ iconName: isLiked ? 'LikeSolid' : 'Like' }} onClick={handleLike} />
                </TooltipHost>
                <TooltipHost content="Dislike">
                    <IconButton iconProps={{ iconName: isDisliked ? 'DislikeSolid' : 'Dislike' }} onClick={handleDislike} />
                </TooltipHost>
            </Stack>

            <LikeFeedback
                isVisible={isLikeDialogVisible}
                onDismiss={handleDialogDismiss}
                onSubmit={handleLikeFeedbackSubmit}
                selectedOptions={selectedLikeOptions}
                setSelectedOptions={setSelectedLikeOptions}
                feedback={feedback}
                setFeedback={setFeedback}
            />

            <DislikeFeedback
                isVisible={isDislikeDialogVisible}
                onDismiss={handleDialogDismiss}
                onSubmit={handleDislikeFeedbackSubmit}
                selectedOptions={selectedDislikeOptions}
                setSelectedOptions={setSelectedDislikeOptions}
                feedback={feedback}
                setFeedback={setFeedback}
            />
        </Stack>
    );
};

export default ResponseAction;

